<template>
  <f7-page class="home-page">
    <template #fixed>
      <MainHeaderComponent type="back" />
    </template>

    <div class="bg-gray title-container">
      <div class="title">
        <font-awesome-icon :icon="['fad', 'cog']" fixed-width />

        <div class="text">
          <h1>{{ $t.getTranslation('LBL_WAREHOUSE_REGION_SAVE') }}</h1>
        </div>
      </div>

      <div class="button-container"></div>
    </div>

    <section class="form-container">
      <div class="container">
        <f7-list no-hairlines>
          <f7-list-item divider>{{ $t.getTranslation('LBL_WAREHOUSE_REGION_DETAILS') }}</f7-list-item>
          <f7-list-input
            v-model:value="formData.WareHouseRegionCode"
            name="WareHouseRegionCode"
            :required="validationRules?.WareHouseRegionCode?.required"
            :minlength="validationRules?.WareHouseRegionCode?.minimumLength"
            :maxlength="validationRules?.WareHouseRegionCode?.maximumLength"
            :label="$t.getTranslation('LBL_WAREHOUSE_REGION_CODE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            :disabled="formData.WareHouseRegionId"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.WareHouseRegionName"
            name="WareHouseRegionName"
            :required="validationRules?.WareHouseRegionName?.required"
            :minlength="validationRules?.WareHouseRegionName?.minimumLength"
            :maxlength="validationRules?.WareHouseRegionName?.maximumLength"
            :label="$t.getTranslation('LBL_WAREHOUSE_REGION_NAME')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.WareHouseRegionOrderCode"
            name="WareHouseRegionOrderCode"
            :required="validationRules?.WareHouseRegionName?.required"
            :minlength="validationRules?.WareHouseRegionName?.minimumLength"
            :maxlength="validationRules?.WareHouseRegionName?.maximumLength"
            :label="$t.getTranslation('LBL_ORDER_WAREHOUSE_REGION_CODE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.Priority"
            name="Priority"
            :required="validationRules?.Priority?.required"
            :minlength="validationRules?.Priority?.minimumLength"
            :maxlength="validationRules?.Priority?.maximumLength"
            :label="$t.getTranslation('LBL_PRIORITY')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.IsCombine"
            name="IsCombine"
            :required="validationRules?.IsCombine?.required"
            :minlength="validationRules?.IsCombine?.minimumLength"
            :maxlength="validationRules?.IsCombine?.maximumLength"
            :label="$t.getTranslation('LBL_IS_COMBINE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_OPTIONAL')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="select"
            validate
          >
            <option v-for="status in statusList" :key="'st_' + status.Code" :value="status.Code">{{ status.Name }}</option>
          </f7-list-input>

          <f7-button large fill raised preloader :loading="isButtonProcessing" :disabled="isButtonProcessing || isDisableSaveButton" @click="save">
            {{ $t.getTranslation('LBL_SAVE') }}
          </f7-button>
        </f7-list>
      </div>
    </section>

    <MainFooterComponent />
  </f7-page>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted, inject } from 'vue'

import { useStore } from '@/store'

import { $HTTP } from '@/utils/axios'
import { helpers } from '@/utils/helpers.js'

import { validations } from '@/utils/validations'
import validationRulesConfig from '@/validationRules'

import MainHeaderComponent from '@/components/MainHeaderComponent.vue'
import MainFooterComponent from '@/components/MainFooterComponent.vue'

export default defineComponent({
  name: 'WareHouseRegionSavePage',
  components: {
    MainHeaderComponent,
    MainFooterComponent
  },
  props: { f7router: Object, f7route: Object },
  setup(props) {
    const $t = inject('$translation')
    const store = useStore()
    const isUpdate = ref(false)
    const isButtonProcessing = ref(false)
    const currentLanguageCode = computed(() => store.getters['translation/getLanguage'])
    const statusList = ref([
      {
        Code: 1,
        Name: $t.getTranslation('LBL_YES')
      },
      {
        Code: 0,
        Name: $t.getTranslation('LBL_NO')
      }
    ])

    const formData = reactive({
      WareHouseCode: null,
      WareHouseName: null,
      WareHouseRegionCode: null
    })

    let validationRules = {}
    for (let keys in formData) {
      if (validationRulesConfig && validationRulesConfig[keys]) {
        validationRules[keys] = validationRulesConfig[keys]
      }
    }

    onMounted(async () => {
      if (props?.f7route?.query?.WareHouseRegionId) {
        await getData()
      }
    })

    const getData = async () => {
      try {
        helpers.showLoader()
        let res = await $HTTP.get(`/admin/warehouse/region/view?WareHouseRegionId=${props?.f7route?.query?.WareHouseRegionId}`)
        if (res?.status === 200 && res?.data?.data) {
          helpers.hideLoader()
          for (var key in res?.data?.data) {
            formData[key] = res?.data?.data[key]
          }

          return
        }
        throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
      } catch (err) {
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    const validate = (showNotifications) => {
      const isValid = validations.validate({
        validateData: formData,
        validationRules: validationRules,
        showNotifications: showNotifications
      })

      return isValid
    }

    const isDisableSaveButton = computed(() => {
      let isValid = validate(false)
      return !isValid
    })

    const save = async () => {
      try {
        let isValid = validate(true)
        if (isValid) {
          isButtonProcessing.value = true
          helpers.showLoader()
          //send update to server
          formData.IsCombine = parseInt(formData.IsCombine)
          let res = await $HTTP.post('/admin/warehouse/region/save', formData)
          if (res?.status === 200 && res?.data?.data) {
            isButtonProcessing.value = false
            helpers.hideLoader()
            //props.f7router.back({ force: true, ignoreCache: true });
            helpers.createNotification({ type: 'success', title: $t.getTranslation('LBL_SUCCESS'), message: $t.getTranslation('LBL_SUCCESS_WAREHOUSE_REGION') })
            props.f7router.navigate({ name: 'warehouseRegionList', query: {} })
            return
          }

          throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
        }
      } catch (err) {
        isButtonProcessing.value = false
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    return { isUpdate, formData, validationRules, isButtonProcessing, isDisableSaveButton, save, statusList }
  }
})
</script>
